/**
 * Getting a finished PDF off the device.
 *
 * Two things the web cannot do, which together decide the design: `mailto:`
 * cannot carry an attachment, and no API can pre-address a share target. So
 * there is no honest "email it to X" button. What does work is
 * `navigator.share({ files })`, which opens the system share sheet with the PDF
 * attached, and a plain download.
 *
 * Downloading is the primary path: the file lands on the phone and gets sent
 * from a mail app later, addressed properly, by a human. Sharing requires HTTPS
 * and a user gesture, and falls back to a download where it is unavailable.
 */

export interface DeliveryOutcome {
  ok: boolean
  /** How it was delivered, for the message shown to the user. */
  method: 'share' | 'download' | 'cancelled' | 'failed'
  message?: string
}

export function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return false
  try {
    const probe = new File([new Blob([new Uint8Array([0])])], 'probe.pdf', { type: 'application/pdf' })
    return navigator.canShare({ files: [probe] })
  } catch {
    return false
  }
}

function fileFrom(pdf: Blob, fileName: string): File {
  return new File([pdf], fileName, { type: 'application/pdf' })
}

/** Opens the system share sheet with the PDF attached. */
export async function sharePdf(
  pdf: Blob,
  fileName: string,
  title: string,
  text: string,
): Promise<DeliveryOutcome> {
  if (!canShareFiles()) {
    downloadPdf(pdf, fileName)
    return { ok: true, method: 'download', message: 'This browser cannot share files, so it was downloaded instead.' }
  }
  try {
    await navigator.share({ files: [fileFrom(pdf, fileName)], title, text })
    return { ok: true, method: 'share' }
  } catch (e) {
    // A user dismissing the sheet throws AbortError, which is not a failure.
    if ((e as Error).name === 'AbortError') return { ok: false, method: 'cancelled' }
    downloadPdf(pdf, fileName)
    return { ok: true, method: 'download', message: 'Sharing failed, so it was downloaded instead.' }
  }
}

export function downloadPdf(pdf: Blob, fileName: string): void {
  const url = URL.createObjectURL(pdf)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoked late: some browsers need the URL alive until the download starts.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function openPdf(pdf: Blob): boolean {
  const url = URL.createObjectURL(pdf)
  const opened = window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return opened !== null
}
